import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import spacing from '@styles/spacing';
import { formatNamedLocation, formatPostcode } from '@utils/format-location';

import { track } from 'lib/sdks/analytics';
import type { Trade } from 'lib/sdks/search';
import ScreenReaderOnly from '@toolshed/styleguide/components/ScreenReaderOnly/ScreenReaderOnly';

import SearchList from './SearchList';

import PostAJobBanner from './PostAJobBanner';
import { List, SearchPageContent, TotalResults } from './styles';

import { isFullPostcode, isPostcode } from '@utils/postcode-validation';
import { Experiment, useOptimizely } from 'lib/sdks/optimizely';

import {
  breakpointMediumStart,
  default100,
  spacing0,
  spacing2,
  spacing3,
  spacing4,
} from 'lib/mortar';
import { HeadingXXL } from 'lib/design-system';

import { useSessionUserId } from '../../../lib/auth/auth.hooks';
import { logBrazeEvent } from '../../../lib/sdks/braze/braze';

import { getDeviceId } from 'lib/utils/device';

import {
  useJobRefinement,
  type AnsweredQuestion,
} from 'components/JobRefinement';
import {
  useJobRefinementState,
  ADD_START_DATE,
} from 'components/JobRefinement/JobRefinement.reducer';
import GuaranteeBanner from './GuaranteeBanner';
import type { SearchPageProps } from './types';
import { Button } from '@checkatrade/web-ui';
import { SearchSorter } from './search-sorter';
import { useIsLeadExcessArea } from './use-is-lead-excess-area';

const formatLocationInput = (string: string) => {
  if (!string) {
    return;
  }

  return isPostcode(string)
    ? formatPostcode(string)
    : formatNamedLocation(string);
};

export const Wrapper = styled.div<StyledDiv>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: ${default100};
`;

export const SearchContentContainer = styled.div<StyledDiv>`
  align-items: flex-start;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: calc(${spacing.maxPageWidth}px - 2 * ${spacing2});
  width: 100%;
  margin: ${spacing0} auto 0;
  min-height: 300px;
  padding: 0 ${spacing4};
  border-radius: ${spacing0};

  @media screen and (min-width: ${breakpointMediumStart}) {
    margin-top: ${spacing3};
  }
`;

export const ScreenReaderOnlyH2 = styled(
  ScreenReaderOnly.withComponent('h2'),
)``;

type SearchContentProps = SearchPageProps & {
  heading?: string;
  showAmbiguousLocations: boolean;
  algorithm?: string;
  setMessageTradeData: (trade: Trade) => void;
  moreTradesLoading: boolean;
  setMoreTradesLoading: (loading: boolean) => void;
  jobRefinementEnabled?: boolean;
  setAlgorithm: Dispatch<SetStateAction<string | undefined>>;
};

const SearchAmbiguousLocationsRedesign = dynamic(
  () => import('./search-ambiguous-locations-redesign'),
);

export const SearchContent = (props: SearchContentProps) => {
  const {
    county,
    data,
    loading,
    location,
    locations,
    onSetModal,
    onMoreTrades,
    onTrackEvent,
    page,
    postcode,
    searchKey,
    defaultToOpen: raqDefaultToOpen,
    heading,
    algorithm,
    showAmbiguousLocations,
    setMessageTradeData,
    moreTradesLoading,
    setMoreTradesLoading,
    jobRefinementEnabled,
    setAlgorithm,
  } = props;

  const localBusiness = data?.meta?.seoData?.localBusiness;

  if (localBusiness) {
    localBusiness.name = localBusiness.name.replaceAll('%LOCATION%', location);
    localBusiness.url = localBusiness.url.replaceAll('%LOCATION%', location);
  }

  const category = useMemo(
    () =>
      props.category || {
        label: '',
        name: '',
      },
    [props.category],
  );

  const defaultMeta = {
    itemsPerPage: undefined,
    currentPage: undefined,
    totalPages: undefined,
    totalResults: undefined,
    experiments: {
      algorithm: [],
    },
  };

  const {
    items = [],
    meta: { itemsPerPage, currentPage, totalPages } = defaultMeta,
  } = data || {
    items: [],
    meta: defaultMeta,
    view: {},
  };

  const userId = useSessionUserId();

  const { openRaqEntryPointJob, openJobSummary } = useJobRefinement();
  const { dispatch } = useJobRefinementState();
  const { decide, loading: optimizelyLoading } = useOptimizely();

  const isLeadExcessArea = useIsLeadExcessArea(
    category.parent?.id ?? category.id,
    postcode,
  );

  const showSearchSorter = useMemo(() => {
    if (!isLeadExcessArea) {
      return false;
    }

    return (
      !optimizelyLoading &&
      decide(Experiment.SEARCH_SORTING)?.variationKey === 'on'
    );
  }, [decide, optimizelyLoading, isLeadExcessArea]);

  const capitalisedLocation = formatLocationInput(location);

  // TODO - Abstract this logic to the V3 Api Response
  const SHOW_RAQ_LIMIT = 3;
  //we will check if we need to hide the RaQ section because of a campaign (for category Central Heating - Id: 10)

  const showRaq = items.length >= SHOW_RAQ_LIMIT;
  useEffect(() => {
    if (category?.name && postcode) {
      logBrazeEvent('ViewedSearchResults', {
        userId,
        categoryName: category.name,
        postcode,
      });
    }
  }, [userId, category?.name, postcode]);

  const contactTrades = (
    payload?: AnsweredQuestion,
    skipSummaryScreen?: boolean,
  ) => {
    if (payload) {
      dispatch({ type: ADD_START_DATE, payload });
    }

    if (skipSummaryScreen) {
      openRaqEntryPointJob('search');
    } else {
      openJobSummary('search');
    }
  };

  const contactTrade = async (trade: Trade) => {
    track('global.message_trader', {
      resource_type: 'search',
      trader_id: trade.companyId,
    });
    const isSearchLocationValid = isFullPostcode(location);
    if (jobRefinementEnabled && isSearchLocationValid) {
      openJobSummary('profile', trade.companyId);
    } else {
      setMessageTradeData(trade);
    }
  };

  const firstLoadedNewTradeId = useMemo(() => {
    if (
      items &&
      currentPage &&
      currentPage > 1 &&
      !moreTradesLoading &&
      itemsPerPage
    ) {
      const newTradesCount = Math.min(
        items.length - (currentPage - 1) * itemsPerPage,
        itemsPerPage,
      );
      return items.slice(-newTradesCount)[0]?.companyId;
    }

    return undefined;
  }, [currentPage, items, itemsPerPage, moreTradesLoading]);

  return (
    <>
      <>
        <TotalResults>
          <HeadingXXL as="h1">{heading}</HeadingXXL>
        </TotalResults>

        {showAmbiguousLocations && (
          <SearchAmbiguousLocationsRedesign
            categoryId={category.id}
            location={location}
            locations={locations}
          />
        )}
      </>

      <SearchContentContainer data-guid="SearchContent">
        <SearchPageContent>
          <List data-guid="SearchList">
            <ScreenReaderOnlyH2>Search results</ScreenReaderOnlyH2>
            {showRaq && (
              <PostAJobBanner
                defaultToOpen={jobRefinementEnabled || raqDefaultToOpen}
                location={location}
                postcode={postcode}
                requestId={props.data.meta.requestId}
                onContactTrades={contactTrades}
              />
            )}
            <GuaranteeBanner postcode={postcode} />{' '}
            {showSearchSorter && (
              <SearchSorter
                categoryName={category.name}
                categoryId={category.id}
                postcode={postcode}
                algorithm={algorithm}
                setAlgorithm={setAlgorithm}
              />
            )}
            <SearchList
              hideContactMessageCta={false}
              operatingLocationText={capitalisedLocation}
              items={items}
              onTrackEvent={onTrackEvent}
              page={page}
              loading={loading}
              itemsPerPage={itemsPerPage}
              postcode={postcode}
              categoryLabel={category.parent?.label || category.label}
              categoryId={category.id}
              location={location}
              onSetModal={onSetModal}
              firstLoadedNewTradeId={firstLoadedNewTradeId}
              onMessageClick={contactTrade}
            />
            {totalPages && currentPage && totalPages > currentPage && (
              <Button
                variant="secondary"
                className="text-xl"
                loading={moreTradesLoading}
                loadingText="Loading tradespeople"
                onClick={() => {
                  track('search.load_more', { current_page: page });
                  setMoreTradesLoading(true);
                  onMoreTrades({
                    page: currentPage + 1,
                    location,
                    category,
                    county,
                    algorithm,
                    key: searchKey,
                    deviceId: getDeviceId(),
                  });
                }}
              >
                Load more tradespeople
              </Button>
            )}
          </List>
        </SearchPageContent>
      </SearchContentContainer>
    </>
  );
};

export default SearchContent;
